import { config } from '../config';
import { fallbackPrice, type Product } from '../design/products';

interface Variant {
  id: number;
  option1: string;
  option2: string;
  option3: string;
  price: number; // minor units
  available: boolean;
}

declare global {
  interface Window {
    Shopify?: { routes?: { root?: string } };
  }
}

/** True when running inside the storefront (same origin as the cart API). */
export const onStorefront = () => typeof window !== 'undefined' && Boolean(window.Shopify);

const root = () => window.Shopify?.routes?.root ?? '/';

let variantsPromise: Promise<Variant[]> | null = null;

export function loadVariants(): Promise<Variant[]> {
  if (!onStorefront()) return Promise.resolve([]);
  variantsPromise ??= fetch(`${root()}products/${config.productHandle}.js`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('product not found'))))
    .then((p) => p.variants as Variant[])
    .catch(() => {
      variantsPromise = null;
      return [];
    });
  return variantsPromise;
}

export const findVariant = (variants: Variant[], p: Product) =>
  variants.find((v) => v.option1 === p.size && v.option2 === p.framing && v.option3 === p.frameColour) ?? null;

export interface Price {
  amount: number | null;
  variantId: number | null;
  available: boolean;
}

export function priceFor(variants: Variant[], p: Product): Price {
  const v = findVariant(variants, p);
  if (v) return { amount: v.price / 100, variantId: v.id, available: v.available };
  // Not on the storefront (local dev) or variants still loading.
  return { amount: fallbackPrice(p), variantId: null, available: fallbackPrice(p) != null };
}

export const formatPrice = (amount: number | null) => (amount == null ? '—' : `£${amount.toFixed(2)}`);

export interface CartLine {
  variantId: number | null;
  properties: Record<string, string>;
}

/** The theme's cart drawer / notification element (Dawn), if the page has one. */
interface ThemeCart extends HTMLElement {
  getSectionsToRender?: () => Array<{ id: string }>;
  renderContents?: (parsedState: unknown) => void;
  setActiveElement?: (el: Element | null) => void;
}
const themeCart = (): ThemeCart | null =>
  (document.querySelector('cart-notification') as ThemeCart | null) ?? (document.querySelector('cart-drawer') as ThemeCart | null);

/**
 * Adds the design to the Shopify cart. Done the way the theme's own product form does it: the
 * request asks Shopify for the cart drawer's refreshed sections and hands them to the theme, so
 * the mini-cart opens showing the design. Returns the cart URL when the theme has no drawer.
 */
export async function addToCart(line: CartLine): Promise<string | null> {
  if (!onStorefront() || line.variantId == null) throw new Error('NOT_ON_STOREFRONT');
  const cart = themeCart();
  const sections = cart?.getSectionsToRender?.().map((s) => s.id) ?? [];
  // One item in the single-item form (not `items: [...]`): Shopify then answers with the line
  // item itself, including the `key` the theme's drawer needs to find the new line.
  const res = await fetch(`${root()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: line.variantId,
      quantity: 1,
      properties: line.properties,
      ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
    }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.description ?? body?.message ?? 'Could not add to cart');
  if (cart?.renderContents && body?.sections) {
    try {
      cart.setActiveElement?.(document.activeElement);
      cart.renderContents(body);
      return null;
    } catch {
      // The item is in the cart; if the drawer can't show it, the cart page can.
    }
  }
  if (await notifyHorizonCart(body)) return null;
  return `${root()}cart`;
}

/**
 * Shopify's newer themes (Horizon, Atelier and the rest of that family) have no drawer API to
 * call; their cart drawer and cart icon listen for a `cart:update` event, which the theme's own
 * product form raises after adding. Raise the same event, then check whether a drawer opened:
 * if it did, the customer can stay on the page.
 */
async function notifyHorizonCart(added: any): Promise<boolean> {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return false;
  try {
    const cartState = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartState,
          sourceId: 'trailmaps-designer',
          data: { source: 'product-form-component', itemCount: cartState.item_count, productId: added?.product_id, variantId: added?.variant_id, didError: false },
        },
      }),
    );
  } catch {
    return false;
  }
  for (let i = 0; i < 15; i++) {
    await new Promise((r) => setTimeout(r, 100));
    if (drawer.querySelector('dialog[open]')) return true;
  }
  return false;
}
